import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import NotificationDropdown from '../shared/NotificationDropdown';
import logo from '../../assets/pictures/logo.png';
import {
  LayoutDashboard,
  Search,
  Users,
  ListFilter,
  Globe,
  GraduationCap,
  FileText,
  BookOpen,
  DollarSign,
  Wallet,
  LogOut,
  Menu,
  X,
  ChevronLeft,
} from 'lucide-react';

// Build menu items dynamically based on base path
function buildMenuItems(basePath) {
  return [
    { path: basePath, icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: `${basePath}/students`, icon: Users, label: 'My Students' },
    { path: `${basePath}/service-requests`, icon: FileText, label: 'Service Requests' },
    { path: `${basePath}/tasks`, icon: ListFilter, label: 'Tasks' },
    { path: `${basePath}/explore`, icon: Search, label: 'Explore Products' },
    { path: `${basePath}/vas`, icon: Globe, label: 'VAS Services' },
    { path: `${basePath}/admissions`, icon: GraduationCap, label: 'Admissions' },
    { path: `${basePath}/applications`, icon: FileText, label: 'Applications' },
    { path: `${basePath}/courses`, icon: BookOpen, label: 'Course Finder' },
    { path: `${basePath}/commission`, icon: DollarSign, label: 'Commission' },
    { path: `${basePath}/earnings`, icon: Wallet, label: 'Earnings' },
  ];
}

export default function AgentLayout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Role-aware configuration: counselor reuses agent infrastructure
  const isCounselor = user?.role === 'counselor';
  const basePath = isCounselor ? '/counselor' : '/agent';
  const portalLabel = isCounselor ? 'Counselor Portal' : 'Agent Portal';
  const roleLabel = isCounselor ? 'Counselor' : 'Agent';
  const headerTitle = isCounselor ? 'Counselor Dashboard' : 'Agent Dashboard';
  const headerSubtitle = isCounselor ? 'Manage your assigned students and cases' : 'Track your referrals and earnings';
  const menuItems = buildMenuItems(basePath);

  return (
    <div
      className="h-screen overflow-hidden bg-[#F8FAFC]"
      data-testid="agent-layout"
    >
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 h-screen flex flex-col bg-white border-r border-gray-200/80 shadow-sm transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${collapsed ? 'w-[72px]' : 'w-[260px]'}`}
        data-testid="agent-sidebar"
      >
        {/* Logo */}
        <div
          className={`h-[64px] px-4 border-b border-gray-200 flex items-center flex-shrink-0 ${collapsed ? 'justify-center' : 'justify-between'}`}
        >
          <Link to={basePath} className="flex items-center gap-3">
            <img
              src={logo}
              alt="Fly8 Logo"
              className="w-16 h-10 object-contain"
            />
            {!collapsed && (
              <div>
                <p className="text-[13px] font-extrabold tracking-widest uppercase bg-gradient-to-r from-gray-700 to-blue-600 bg-clip-text text-transparent">
                  {portalLabel}
                </p>
              </div>
            )}
          </Link>

          <button
            className={`hidden lg:flex items-center justify-center w-7 h-7 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 ${collapsed ? 'absolute right-2' : ''}`}
            onClick={() => setCollapsed(!collapsed)}
          >
            <ChevronLeft
              className={`w-4 h-4 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}
            />
          </button>

          <button
            className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu */}
        <nav
          className="flex-1 px-3 py-2 space-y-1 overflow-hidden"
          data-testid="agent-menu"
        >
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = item.exact
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path) &&
                location.pathname !== basePath;

            const isExactDashboard =
              item.exact && location.pathname === item.path;
            const finalActive = item.exact ? isExactDashboard : isActive;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group relative overflow-hidden flex items-center gap-3 px-3 py-[6px] rounded-xl font-medium transition-all duration-200 before:content-[''] before:absolute before:inset-0 before:rounded-xl before:bg-emerald-600 before:translate-x-full before:transition-transform before:duration-300 before:ease-out hover:before:translate-x-0 hover:shadow-md hover:shadow-emerald-600/20 ${
                  finalActive
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20'
                    : 'text-gray-600 hover:text-white'
                } ${collapsed ? 'justify-center px-2.5' : ''}`}
                onClick={() => setSidebarOpen(false)}
                title={collapsed ? item.label : undefined}
                data-testid={`agent-menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3 relative z-10">
                  <span
                    className={`w-8 h-8 flex items-center justify-center rounded-xl transition-all duration-200 ${
                      finalActive
                        ? 'bg-white/20 shadow-[0_0_12px_rgba(255,255,255,0.3)]'
                        : 'bg-emerald-50 shadow-sm shadow-emerald-100 group-hover:bg-white/20 group-hover:shadow-[0_0_12px_rgba(255,255,255,0.2)]'
                    }`}
                  >
                    <Icon
                      className={`w-[17px] h-[17px] transition-all duration-200 ${
                        finalActive
                          ? 'text-white'
                          : 'text-emerald-600 group-hover:text-white'
                      }`}
                    />
                  </span>
                  {!collapsed && (
                    <span className="text-[14px] flex-1 relative z-10">
                      {item.label}
                    </span>
                  )}
                </div>
                {!collapsed && item.badge && (
                  <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 font-bold rounded-full relative z-10 min-w-[20px] text-center">
                    {item.badge}
                  </Badge>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div
          className={`flex-shrink-0 p-3 border-t border-gray-100 ${collapsed ? 'flex flex-col items-center' : ''}`}
        >
          {!collapsed && (
            <div className="flex items-center gap-3 mb-3 p-3 rounded-xl bg-emerald-50/50 border border-emerald-100/50">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-400 rounded-xl flex items-center justify-center text-white text-sm font-bold shadow-sm">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate text-gray-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-emerald-600 font-medium">{roleLabel}</p>
              </div>
            </div>
          )}
          <Button
            onClick={logout}
            variant="ghost"
            className={`w-full flex items-center border border-emerald-200/40 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 active:bg-emerald-100 transition-all duration-200 ease-in-out rounded-xl shadow-sm shadow-emerald-300/20 ${collapsed ? 'p-2.5 justify-center' : 'px-3 py-2 justify-start'}`}
            size="sm"
            data-testid="agent-logout-button"
          >
            <LogOut className={`w-4 h-4 ${collapsed ? '' : 'mr-2'}`} />
            {!collapsed && 'Logout'}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`h-screen flex flex-col transition-all duration-300 ${collapsed ? 'lg:ml-[72px]' : 'lg:ml-[260px]'}`}
      >
        {/* Top Bar */}
        <header
          className="h-[64px] flex-shrink-0 bg-white/80  backdrop-blur-md border-b border-gray-200/80 px-4 lg:px-8 flex items-center sticky top-0 z-30"
          data-testid="agent-header"
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-lg font-bold font-outfit text-gray-900">
                  {headerTitle}
                </h2>
                <p className="text-xs text-gray-400 hidden sm:block">
                  {headerSubtitle}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-1.5 border border-gray-200 rounded-xl text-sm w-64 bg-gray-50/80 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 focus:bg-white transition-colors"
                  data-testid="agent-search"
                />
              </div>
              <NotificationDropdown />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 bg-indigo-50">
          {children}
        </main>
      </div>
    </div>
  );
}
